import { create } from 'zustand';

type Theme = 'light' | 'dark' | 'system';

interface UIState {
  theme: Theme;
  sidebarOpen: boolean;
  modalOpen: boolean;
  modalContent: React.ReactNode | null;
  toasts: Toast[];
  loading: boolean;
  setTheme: (theme: Theme) => void;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  openModal: (content: React.ReactNode) => void;
  closeModal: () => void;
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  setLoading: (loading: boolean) => void;
}

export interface Toast {
  id: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
}

let toastId = 0;

/** SSR/test-safe theme read — localStorage may be unavailable (node env). */
function readTheme(): Theme {
  try {
    return (localStorage.getItem('bp-theme') as Theme) || 'system';
  } catch {
    return 'system';
  }
}

export const useUIStore = create<UIState>((set) => ({
  theme: readTheme(),
  sidebarOpen: false,
  modalOpen: false,
  modalContent: null,
  toasts: [],
  loading: false,

  setTheme: (theme) => {
    try {
      localStorage.setItem('bp-theme', theme);
    } catch {
      /* storage unavailable — theme not persisted */
    }
    set({ theme });
  },

  toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),

  setSidebarOpen: (open) => set({ sidebarOpen: open }),

  openModal: (content) => set({ modalOpen: true, modalContent: content }),

  closeModal: () => set({ modalOpen: false, modalContent: null }),

  addToast: (toast) => {
    const id = `toast-${++toastId}`;
    set((state) => ({ toasts: [...state.toasts, { ...toast, id }] }));

    if (toast.duration !== 0) {
      setTimeout(() => {
        set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }));
      }, toast.duration || 5000);
    }
  },

  removeToast: (id) =>
    set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),

  setLoading: (loading) => set({ loading }),
}));
